import React from 'react'

import { useAuth } from '../../context/auth'
import { updateUserLocale } from '../../api'
import PageSectionTitle from '../ui/pageSectionTitle'
import { useAlerts } from '../../context/alerts'

const LOCALE_OPTIONS: { value: string; label: string }[] = [
  { value: 'en-GB', label: 'English (UK) — dd/mm/yyyy' },
  { value: 'en-US', label: 'English (US) — mm/dd/yyyy' },
  { value: 'es-ES', label: 'Español (España) — dd/mm/yyyy' },
  { value: 'fr-FR', label: 'Français (France) — dd/mm/yyyy' },
  { value: 'de-DE', label: 'Deutsch (Deutschland) — dd.mm.yyyy' },
  { value: 'it-IT', label: 'Italiano (Italia) — dd/mm/yyyy' },
  { value: 'pt-BR', label: 'Português (Brasil) — dd/mm/yyyy' },
  { value: 'pt-PT', label: 'Português (Portugal) — dd/mm/yyyy' },
]

const DEFAULT_LOCALE = LOCALE_OPTIONS[0].value
const optionValues = LOCALE_OPTIONS.map((o) => o.value)

const resolveLocale = (value: string | undefined) =>
  value && optionValues.includes(value) ? value : DEFAULT_LOCALE

const LocaleForm = () => {
  const { user, mutate } = useAuth()
  const { addAlert } = useAlerts()
  const [locale, setLocale] = React.useState(resolveLocale(user?.locale))
  const [isSaving, setIsSaving] = React.useState(false)

  React.useEffect(() => {
    if (user?.locale !== undefined) {
      setLocale(resolveLocale(user.locale))
    }
  }, [user?.locale])

  const handleLocaleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextLocale = e.target.value
    if (nextLocale === resolveLocale(user?.locale)) {
      setLocale(nextLocale)
      return
    }

    setLocale(nextLocale)
    setIsSaving(true)
    const res = await updateUserLocale(nextLocale)
    if (res?.data) {
      await mutate()
      addAlert({ type: 'info', message: 'Date format updated' })
    } else {
      setLocale(resolveLocale(user?.locale))
      addAlert({ type: 'error', message: 'Could not update date format' })
    }
    setIsSaving(false)
  }

  return (
    <div>
      <PageSectionTitle
        title="Date & number format"
        description="Used for dates in the app and in emails sent to your clients."
      />
      <div className="mt-3">
        <label htmlFor="locale" className="form-label">
          Locale
        </label>
        <select
          id="locale"
          className="form-select"
          value={locale}
          onChange={handleLocaleChange}
          disabled={isSaving}
          style={{ maxWidth: '320px' }}
        >
          {LOCALE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}

export default LocaleForm
